// Large files go from the browser straight to S3 (backend: routers/direct_uploads.py). The
// backend opens a multipart upload, signs one URL per part and completes; the bytes never pass
// through it.
//
//   directUpload({ datasetId, file, replace, onProgress, onStatus, signal }) -> DataFileOut
//
// 1. Resume or open: an open upload of the same name and size in this dataset (GET
//    /datasets/{id}/direct-uploads) is resumed, so a reload or a dropped network loses only the
//    parts in flight; otherwise one is opened (POST, which runs the usual checks: 409 name taken,
//    413 too large, 429 too many).
// 2. Each part is read from the local file and its MD5 computed HERE (spark-md5). A part S3
//    already holds with that MD5 as its ETag is skipped; any other part is signed with its MD5
//    (the URL then accepts only those bytes) and PUT, `CONCURRENCY` at a time, each retried with
//    back-off and a fresh signature.
// 3. Complete sends every part's MD5; the backend reads the stored object back and refuses it
//    (422, nothing kept) unless every part matches what this browser read from the file.
//
// The same function serves any dataset-file upload (the Files tab today; a live-database file
// field later): it needs only a dataset id and a File.

import SparkMD5 from "spark-md5";
import { api } from "../api";

export const CONCURRENCY = 4;
export const MAX_ATTEMPTS = 6;

export function retryDelay(attempt) {
  return Math.min(30000, 1000 * 2 ** attempt);
}

/** The name the server stores (storage.safe_filename), to recognise an upload to resume. */
export function safeFilename(name) {
  const base = String(name || "").replace(/\\/g, "/").split("/").pop().trim();
  const cleaned = base.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^[._]+|[._]+$/g, "");
  return (cleaned || "file").slice(0, 200);
}

export function partRanges(size, partSize) {
  const count = Math.max(1, Math.ceil(size / partSize));
  return Array.from({ length: count }, (_, i) => ({
    number: i + 1, start: i * partSize, end: Math.min(size, (i + 1) * partSize),
  }));
}

const sleepFor = ms => new Promise(r => setTimeout(r, ms));

async function findOpen(datasetId, file, replace) {
  const name = safeFilename(file.name);
  const open = await api.get(`/datasets/${datasetId}/direct-uploads`);
  return (open || []).find(u => u.filename === name && u.size === file.size && !!u.replace === !!replace
                                && !u.completing) || null;
}

export async function directUpload({ datasetId, file, replace = false, onProgress, onStatus, signal,
                                     sleep = sleepFor }) {
  const status = s => onStatus && onStatus(s);
  status("checksum");
  let up = await findOpen(datasetId, file, replace);
  let stored = {};
  if (up) {
    const st = await api.get(`/direct-uploads/${up.id}`);
    (st.parts || []).forEach(p => { stored[p.number] = { etag: String(p.etag).replace(/"/g, ""), size: p.size }; });
    up = st;
  } else {
    up = await api.post(`/datasets/${datasetId}/direct-uploads`, {
      filename: file.name, size: file.size, content_type: file.type || null, replace,
    });
  }

  const ranges = partRanges(file.size, up.part_size);
  const md5s = new Array(ranges.length);
  const loaded = new Array(ranges.length).fill(0);
  const report = () => onProgress && onProgress(loaded.reduce((a, b) => a + b, 0) / file.size);
  status("uploading");

  const sendPart = async ({ number, start, end }) => {
    const buf = await file.slice(start, end).arrayBuffer();
    const md5 = SparkMD5.ArrayBuffer.hash(buf);
    md5s[number - 1] = md5;
    const have = stored[number];
    if (have && have.etag === md5 && have.size === end - start) {
      loaded[number - 1] = end - start;
      report();
      return;
    }
    for (let attempt = 0; ; attempt += 1) {
      if (signal && signal.aborted) throw Object.assign(new Error("The upload was cancelled."), { status: -1 });
      try {
        const signed = await api.post(`/direct-uploads/${up.id}/parts`, { parts: [{ number, md5 }] });
        const p = signed.parts[0];
        await api.putPart(p.url, buf, {
          headers: p.headers, signal,
          onProgress: n => { loaded[number - 1] = n; report(); },
        });
        loaded[number - 1] = end - start;
        report();
        return;
      } catch (err) {
        loaded[number - 1] = 0;
        report();
        // A cancel, or the portal itself refusing (401/403/404/422 from /api): no point retrying.
        // Storage answers (err.storage) and network errors are retried with a fresh signature.
        if (err.status === -1 || (!err.storage && err.status > 0 && err.status < 500)) throw err;
        if (attempt + 1 >= MAX_ATTEMPTS) {
          throw new Error(`Part ${number} of ${ranges.length} could not be uploaded (${err.message}). `
            + "What arrived is kept: choose the same file again to resume. If this happens at once "
            + "every time, the storage may not accept uploads from this page; an administrator "
            + "checks it in Management → Storage.");
        }
        await sleep(retryDelay(attempt));
      }
    }
  };

  const queue = [...ranges];
  const worker = async () => {
    while (queue.length) await sendPart(queue.shift());
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));

  status("verifying");
  return api.post(`/direct-uploads/${up.id}/complete`, {
    parts: md5s.map((md5, i) => ({ number: i + 1, md5 })),
  });
}
